import type { PropsWithChildren } from 'react'
import { useEffect, useRef, useState } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import LineExclamationCircleIcon from 'LuxKit/Icons/line/LineExclamationCircleIcon'
import TextButton from '../../../../LuxKit/Button/TextButton'
import TextLink from '../../../../LuxKit/TextLink'
import { useIntl, FormattedMessage } from 'react-intl'

const ErrorContainer = styled.div`
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: ${rem(32)} ${rem(12)};

  > * + * {
    margin-top: ${rem(20)};
  }
`
const StyledHeading = styled(Heading)`
  max-width: ${rem(700)};
`

const StyledSubtitle = styled(BodyText)`
  max-width: ${rem(700)};
`

interface Props {
  className?: string
  onRefresh?: (e: React.MouseEvent<HTMLButtonElement>) => void
}

const onRefreshFn = () => window.location.reload()

const TRANSLATION_DETECTION_TIME_LIMIT = 10000 // 10 sec

function OhNoRefreshError(props: PropsWithChildren<Props>) {
  const {
    children,
    onRefresh = onRefreshFn,
    className,
  } = props
  const intl = useIntl()

  const headingRef = useRef<HTMLDivElement>(null)

  const headingText = intl.formatMessage({
    id: 'eQ2czyW2BCwYWH25uSJp',
    defaultMessage: 'Oh no, an error has occurred',
  })

  // Detect use of Google Translate or other externally-imposed translation methods. They can
  // cause errors, so we should advise the user that this might be why the error occurred.
  // More info: https://martijnhols.nl/blog/everything-about-google-translate-crashing-react
  const [showTranslationError, setShowTranslationError] = useState(false)
  useEffect(() => {
    const startTime = Date.now()
    const interval = setInterval(() => {
      const currentHeadingText = headingRef.current?.innerText

      if (currentHeadingText !== headingText) {
        setShowTranslationError(true)
      }

      if (Date.now() - startTime > TRANSLATION_DETECTION_TIME_LIMIT) {
        clearInterval(interval)
      }
    }, 500)

    return () => clearInterval(interval)
  }, [headingText])

  return (
    <ErrorContainer className={className}>
      <LineExclamationCircleIcon size="XL" />
      <StyledHeading variant="heading2" align="center" forwardedAs="h1" ref={headingRef}>
        {headingText}
      </StyledHeading>
      <StyledSubtitle variant="large" align="center" forwardedAs="h2">
        {children}
        {!children && <FormattedMessage
          id="b-2q1lcKOs0DfG28DyZX"
          defaultMessage="Our team are working on fixing it now. Please try refreshing the page or visiting our <link>homepage</link> for our latest offers."
          
          values={{
            link: (chunks) => <TextLink href="/" target="_blank" rel="noopener nofollow">{chunks}</TextLink>,
          }}
        />}
      </StyledSubtitle>
      {showTranslationError && <StyledSubtitle variant="large">
        <FormattedMessage
          id="JNVA5ZLaEU8FCWnaxXuv"
          defaultMessage="If you are using Google Translate or similar, try turning it off as it may cause errors."
          
        />
      </StyledSubtitle>}
      <TextButton
        kind="primary"
        onClick={onRefresh}
        fit="mobile-full-width"
      >
        {intl.formatMessage({
          id: 'ILGXC_yarVjWkFAjhhA4',
          defaultMessage: 'Refresh',
        })}
      </TextButton>
    </ErrorContainer>
  )
}

export default OhNoRefreshError
